import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipForward, Volume2, VolumeX, X } from 'lucide-react';
import { EASE } from '../data/site';

const TRACKS = [
  { title: 'Found Object Groove', src: '/assets/music/found-object-groove.mp3' },
  { title: 'Market Focus', src: '/assets/music/market-focus.mp3' },
  { title: 'Sukshma Manas', src: '/assets/music/sukshma-manas.mp3' },
  { title: 'Market Pulse', src: '/assets/music/market-pulse.mp3' },
  { title: 'Found Object Groove II', src: '/assets/music/found-object-groove-ii.mp3' },
];

const Eq = ({ active, className = 'bg-brand-amber' }) => (
  <span className="flex items-end gap-[2.5px] h-3" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className={`w-[2.5px] rounded-full ${className} ${active ? 'animate-eq' : 'opacity-40'}`}
        style={active ? { animationDelay: `${i * 0.16}s` } : { height: 3 }}
      />
    ))}
  </span>
);

const MusicPlayer = () => {
  const audioRef = useRef(null);
  const [trackIdx, setTrackIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState(false);
  const dragRef = useRef(false);

  useEffect(() => {
    const audio = new Audio();
    audio.volume = 0.55;
    audio.src = TRACKS[0].src;
    audioRef.current = audio;
    const onEnded = () => setTrackIdx((i) => (i + 1) % TRACKS.length);
    audio.addEventListener('ended', onEnded);
    const hintIn = setTimeout(() => setHint(true), 2500);
    const hintOut = setTimeout(() => setHint(false), 9000);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.pause();
      clearTimeout(hintIn);
      clearTimeout(hintOut);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = TRACKS[trackIdx].src;
    if (playing) audio.play().catch(() => setPlaying(false));
  }, [trackIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const next = () => setTrackIdx((i) => (i + 1) % TRACKS.length);

  return (
    <motion.div
      drag
      dragMomentum={false}
      onDragStart={() => { dragRef.current = true; }}
      onDragEnd={() => { setTimeout(() => { dragRef.current = false; }, 60); }}
      dragConstraints={{ left: -(typeof window !== 'undefined' ? window.innerWidth - 120 : 0), right: 0, top: -(typeof window !== 'undefined' ? window.innerHeight - 100 : 0), bottom: 0 }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.8, duration: 0.7, ease: EASE }}
      className="fixed bottom-5 right-5 z-[85] select-none"
      data-testid="music-player"
    >
      <AnimatePresence>
        {hint && !open && (
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute -top-9 right-0 whitespace-nowrap rounded-full bg-brand-black/90 text-paper/85 px-3.5 py-1.5 text-[10px] tracking-wide backdrop-blur pointer-events-none"
            data-testid="player-hint"
          >
            Random AI music, not the best, but keeps you going
          </motion.p>
        )}
      </AnimatePresence>

      <motion.div
        layout
        transition={{ duration: 0.35, ease: EASE }}
        className="flex items-center gap-1 rounded-full bg-brand-black/90 text-paper shadow-lg backdrop-blur border border-white/10 overflow-hidden"
        style={{ height: 40 }}
      >
        <button
          onClick={() => { if (!dragRef.current) togglePlay(); }}
          aria-label={playing ? 'Pause' : 'Play'}
          data-testid="player-play-toggle"
          className="flex h-10 w-10 shrink-0 items-center justify-center text-paper/90 transition-colors hover:text-brand-amber"
        >
          {playing ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="controls"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="flex items-center gap-1 overflow-hidden"
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={trackIdx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="text-[11px] font-medium tracking-tight whitespace-nowrap max-w-[110px] truncate pl-1"
                  data-testid="player-track-title"
                  title={`${TRACKS[trackIdx].title}, random AI music, not the best, but keeps you going`}
                >
                  {TRACKS[trackIdx].title}
                </motion.span>
              </AnimatePresence>
              <button onClick={next} aria-label="Next track" data-testid="player-next" className="flex h-8 w-8 shrink-0 items-center justify-center text-paper/60 transition-colors hover:text-paper">
                <SkipForward size={12} />
              </button>
              <button onClick={() => setMuted(!muted)} aria-label={muted ? 'Unmute' : 'Mute'} data-testid="player-mute" className="flex h-8 w-8 shrink-0 items-center justify-center text-paper/60 transition-colors hover:text-paper">
                {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={() => { if (!dragRef.current) setOpen(!open); }}
          aria-label={open ? 'Collapse music player' : 'Expand music player'}
          data-testid="player-expand"
          className="flex h-10 w-9 shrink-0 items-center justify-center"
        >
          {open ? <X size={12} className="text-paper/50" /> : <Eq active={playing && !muted} />}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default MusicPlayer;
